import React, { FC, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faGripDotsVertical, faLink, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { defineMessages, useIntl } from "react-intl";

import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { Button, fileTypeToIcons, getFileType, Modal } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import { getPinnedResources } from "../../store";
import { updatePinnedResources } from "../../store/actions";

import styles from "./ReorderModal.module.scss";

const messages = defineMessages({
  description: {
    id: "resources.pinned.reorder.modal.description",
    defaultMessage:
      "Pinned resources display at the top in this order on both web and mobile. Removing a resource here does not delete the resource.",
    description: "Reorder pinned resources modal description",
  },
  done: {
    id: "resources.pinned.reorder.modal.done",
    defaultMessage: "Done",
    description: "Reorder pinned resources modal Done button",
  },
  reorder: {
    id: "resources.pinned.reorder",
    defaultMessage: "Reorder",
    description: "Reorder pinned resources button",
  },
  title: {
    id: "resources.pinned.reorder.modal.title",
    defaultMessage: "Reorder Pinned Resources",
    description: "Reorder pinned resources modal title",
  },
});

export interface ReorderModalProps {
  onHide: () => void;
  onUpdatePinnedResources: typeof updatePinnedResources.request;
  open: boolean;
  resources: Resource[];
}

const ReorderModal: FC<ReorderModalProps> = ({
  onHide,
  onUpdatePinnedResources,
  open,
  resources,
}) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Reorder Pins", useAnalytics());

  const [pinnedResources, setPinnedResources] = useState(resources);

  const handleDragEnd = useCallback(
    ({ destination, source }) => {
      if (!destination) {
        return;
      }

      const startIndex = source.index;
      const endIndex = destination.index;

      if (startIndex === endIndex) {
        return;
      }

      const updatedResources = Array.from(pinnedResources);
      const [movedItem] = updatedResources.splice(startIndex, 1);
      updatedResources.splice(endIndex, 0, movedItem);

      setPinnedResources(updatedResources);
    },
    [pinnedResources]
  );

  const handleConfirm = useCallback(() => {
    onUpdatePinnedResources({
      callback: () => {
        onHide();
        if (pinnedResources.length !== resources.length) {
          analytics.completed(["Unpin"]);
        }
        analytics.completed([]);
      },
      ids: pinnedResources.map(({ id }) => id),
    });
  }, [analytics, onHide, onUpdatePinnedResources, pinnedResources, resources.length]);

  const handleRemove = useCallback((id: Resource["id"]) => {
    setPinnedResources((resources) => resources.filter((resource) => resource.id !== id));
  }, []);

  const handleCancel = useCallback(() => {
    onHide();
    setPinnedResources(resources);
  }, [onHide, resources]);

  useEffect(() => {
    setPinnedResources(resources);
  }, [resources]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Modal
        okText={intl.formatMessage(messages.done)}
        onCancel={handleCancel}
        onOk={handleConfirm}
        open={open}
        title={intl.formatMessage(messages.title)}
        width="700px"
      >
        <div className={styles.container}>
          <span className={styles.description}>{intl.formatMessage(messages.description)}</span>
          <Droppable droppableId="droppable">
            {(provided) =>
              pinnedResources.length > 0 ? (
                <div className={styles.fields} {...provided.droppableProps} ref={provided.innerRef}>
                  {pinnedResources.map((resource, index) => (
                    <Draggable draggableId={resource.name} index={index} key={resource.id}>
                      {(provided) => (
                        <div
                          className={cn(styles.draggable, {
                            [styles.lastItem]: index === pinnedResources.length - 1,
                          })}
                          {...(provided ? provided.draggableProps : {})}
                          ref={provided ? provided.innerRef : undefined}
                        >
                          <div className={styles.left}>
                            <div
                              className={styles.handle}
                              tabIndex={-1}
                              {...provided.dragHandleProps}
                            >
                              <FontAwesomeIcon icon={faGripDotsVertical} />
                            </div>
                            <FontAwesomeIcon
                              className={styles.fileIcon}
                              icon={
                                resource.file
                                  ? fileTypeToIcons[getFileType(resource.file)].solid
                                  : faLink
                              }
                            />
                            <span className={styles.name}>{resource.name}</span>
                          </div>
                          <Button onClick={() => handleRemove(resource.id)} type="control">
                            <FontAwesomeIcon className={styles.remove} icon={faXmark} size="sm" />
                          </Button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              ) : (
                <></>
              )
            }
          </Droppable>
        </div>
      </Modal>
    </DragDropContext>
  );
};

const mapStateToProps = (state: RootState) => ({
  resources: getPinnedResources(state),
});

const mapDispatchToProps = {
  onUpdatePinnedResources: updatePinnedResources.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReorderModal);
